import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency, formatDate } from '@/helpers/billing'
import { OneTimeFee } from '@/types/billing'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import { Check, CreditCard, X } from 'lucide-react'
import { showNotification } from '@/services/notificationService'
import { createOneTimeFeeCheckout } from '@/services/billingService'
import { Skeleton } from '@/components/ui/skeleton'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Pagination } from '@/components/custom/Pagination/Pagination'

interface OneTimeFeesProps {
  fees?: OneTimeFee[]
  onRefresh: () => void
  isLoading: boolean
}

export const OneTimeFees: React.FC<OneTimeFeesProps> = ({ fees, onRefresh, isLoading }) => {
  const [limit, setLimit] = useState(5) // default 5 items per page
  const [offset, setOffset] = useState(0)

  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const [payingFeeId, setPayingFeeId] = useState<string | null>(null)

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800 hover:text-background',
    checkout_open: 'bg-blue-100 text-blue-800 hover:text-background',
    paid: 'bg-green-100 text-green-800 hover:text-background',
    invoiced: 'bg-purple-100 text-purple-800 hover:text-background',
    voided: 'bg-red-100 text-red-800 hover:text-background',
    waived: 'bg-gray-100 text-gray-800 hover:text-background',
    failed: 'bg-red-100 text-red-800 hover:text-background'
  }

  const handlePayNow = async (feeId: string) => {
    if (!token) return
    try {
      setPayingFeeId(feeId)
      const response = await dispatch(createOneTimeFeeCheckout(token, feeId))
      if (response.data?.url) window.location.href = response.data.url
      else console.error('No session URL in response:', response.err)
    } catch (error) {
      console.error('Error creating one-time fee checkout:', error)
      showNotification({ data: undefined, err: 'Failed to create one-time fee' }, '', {})
    } finally {
      setPayingFeeId(null)
    }
  }

  const getFeeAmount = (fee: OneTimeFee) => Number(fee.amountCents) / 100

  const renderSkeleton = () => (
    <div className="space-y-4">
      {Array.from({ length: 2 }).map((_, idx) => (
        <div key={idx} className="border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className='flex justify-between'>
            <div className="flex items-center gap-3 mb-4">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-5 w-12" />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <Skeleton className="h-6 w-24" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-3">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className='w-full'>
            <Skeleton className="h-3 w-1/2 mb-2" />
          </div>
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  )

  const renderHeader = () => (
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-base font-semibold text-gray-900">One-Time Fees</h2>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <svg className="w-3.5 h-3.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Refresh
      </Button>
    </div>
  )

  const renderEmpty = () => (
    <div className="text-center py-8">
      <p className="text-sm text-gray-500">No one-time fees</p>
    </div>
  )

  const renderFeeCard = (fee: OneTimeFee) => (
    <div key={fee.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors mt-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="font-medium text-gray-900">{fee.label}</h3>
            <TooltipProvider>
              <Badge className={statusColors[fee.status]}>
                {fee.status}
              </Badge>
            </TooltipProvider>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="flex flex-col items-start">
              <span className="text-gray-600 font-semibold">Amount:</span>
              <div className="font-medium text-lg text-gray-900">{formatCurrency(getFeeAmount(fee))}</div>
            </div>
            <div className="flex flex-col items-start">
              <span className="text-gray-600 font-semibold">Created:</span>
              <div className="font-medium text-gray-900">{formatDate(fee.dateCreated)}</div>
            </div>
            {fee.paidAt && (
              <div className="flex flex-col items-start">
                <span className="text-gray-600 font-semibold">Paid:</span>
                <div className="font-medium text-gray-900">{formatDate(fee.paidAt)}</div>
              </div>
            )}
          </div>

          {fee.notes && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
              <span className="text-gray-600">Note:</span> {fee.notes}
            </div>
          )}
        </div>

        <div className="flex gap-2 ml-4">
          {fee.status === 'checkout_open' && (
            <Button
              size="sm"
              onClick={() => handlePayNow(fee.id)}
              disabled={payingFeeId === fee.id}
              className="bg-green-600 hover:bg-green-700"
            >
              {payingFeeId === fee.id ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" /> Loading...
                </div>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-1" /> Pay Now
                </>
              )}
            </Button>
          )}

          {fee.promotionCodeId && <div className="text-xs text-green-600">Promo code applied</div>}
        </div>
      </div>

      {fee.status === 'checkout_open' && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm flex items-center text-center">
            <CreditCard className="w-4 h-4 mr-2" /> Ready for payment. Click "Pay Now" to complete this one-time fee.
            {fee.promotionCodeId && ' (Promotion codes can be applied at checkout)'}
          </p>
        </div>
      )}

      {fee.status === 'failed' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm flex items-center text-center">
            <X className="w-4 h-4 mr-2" /> Payment failed. Please try again or contact support.
          </p>
        </div>
      )}

      {fee.status === 'waived' && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-700 text-sm flex items-center text-center">
            <Check className="h-4 w-4 mr-2" /> This fee has been waived by an administrator.
          </p>
        </div>
      )}
    </div>
  )

  // slice invoices for current page
  const paginatedFees = fees?.slice(offset, offset + limit)

  return (
    <div className="px-6 py-5">
      {renderHeader()}
      {isLoading ? renderSkeleton() : (fees?.length ?? 0) > 0 ? (
        <>
          <div className="space-y-4">{paginatedFees?.map(renderFeeCard)}</div>
          <div className="mt-4">
            <Pagination
              total={fees?.length || 0}
              limit={limit}
              offset={offset}
              onPageChange={setOffset}
              onLimitChange={(newLimit) => {
                setLimit(newLimit)
                setOffset(0)
              }}
            />
          </div>
        </>
      ) : renderEmpty()}
    </div>
  )
}
